import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
} from 'firebase/firestore';
import { db } from '../firebase/config';

export interface HumanDatasetPair {
  cardId: string;
  docId: string;
  prompt: string;
  response: string;
  sessionId?: string;
  sessionTitle?: string;
  sessionIndex?: number;
}

const HUMAN_SESSIONS_COLLECTION = 'human-meets-ai-sessions';
const HUMAN_CHATBOT_COLLECTION = 'human-meets-ai-chatbot';

let cachedMaxSessionIndex: number | null = null;

async function ensureMaxSessionIndex(): Promise<number | null> {
  if (cachedMaxSessionIndex !== null) {
    return cachedMaxSessionIndex;
  }

  const sessionsRef = collection(db, HUMAN_SESSIONS_COLLECTION);
  const snapshot = await getDocs(query(sessionsRef, orderBy('sessionIndex', 'desc'), limit(1)));

  if (snapshot.empty) {
    cachedMaxSessionIndex = null;
    return null;
  }

  const data = snapshot.docs[0].data() as { sessionIndex?: number };
  cachedMaxSessionIndex = typeof data.sessionIndex === 'number' ? data.sessionIndex : null;
  return cachedMaxSessionIndex;
}

/**
 * Fetch a random first-in-session prompt/response pair from Firestore.
 * Attempts up to `maxAttempts` times to locate a valid pair that includes both prompt and response text.
 */
export async function fetchRandomFirstSessionPair(
  maxAttempts = 5,
): Promise<HumanDatasetPair | null> {
  const maxSessionIndex = await ensureMaxSessionIndex();
  if (maxSessionIndex === null) {
    console.warn('No sessions available in human-meets-ai-sessions collection.');
    return null;
  }

  const sessionsRef = collection(db, HUMAN_SESSIONS_COLLECTION);

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const randomIndex = Math.floor(Math.random() * (maxSessionIndex + 1));
    const sessionSnapshot = await getDocs(
      query(sessionsRef, orderBy('sessionIndex'), startAt(randomIndex), limit(1)),
    );

    if (sessionSnapshot.empty) {
      continue;
    }

    const sessionDoc = sessionSnapshot.docs[0];
    const sessionData = sessionDoc.data() as {
      firstCardId?: string;
      cardIds?: string[];
      sessionId?: string;
      title?: string;
      sessionIndex?: number;
    };

    const firstCardId =
      sessionData.firstCardId ??
      (Array.isArray(sessionData.cardIds) && sessionData.cardIds.length > 0
        ? sessionData.cardIds[0]
        : undefined);

    if (!firstCardId) {
      continue;
    }

    const baselineDocId = `baseline-${firstCardId}`;
    const baselineDoc = await getDoc(doc(db, HUMAN_CHATBOT_COLLECTION, baselineDocId));

    if (!baselineDoc.exists()) {
      continue;
    }

    const baselineData = baselineDoc.data() as {
      prompt?: string;
      response?: string;
      sessionTitle?: string;
    };

    if (!baselineData?.prompt || !baselineData?.response) {
      continue;
    }

    return {
      cardId: firstCardId,
      docId: baselineDocId,
      prompt: baselineData.prompt,
      response: baselineData.response,
      sessionId: sessionData.sessionId ?? sessionDoc.id,
      sessionTitle: sessionData.title ?? baselineData.sessionTitle,
      sessionIndex: sessionData.sessionIndex,
    };
  }

  console.warn(
    `Unable to locate a valid first-in-session prompt after ${maxAttempts} attempt(s).`,
  );
  return null;
}


